<script lang="ts" setup>
import type { FetchError } from 'ofetch'

import dayjs from 'dayjs'
import LineInfoCircle from '@docue/docue-ui-v2/icons/LineInfoCircle.vue'
import LineAlertCircle from '@docue/docue-ui-v2/icons/LineAlertCircle.vue'
import LineReverseLeft from '@docue/docue-ui-v2/icons/LineReverseLeft.vue'

import type { InvoicingData } from '~/components/Billing/BillingInvoiceForm.vue'
import type { BillingDetails } from '~/components/Billing/BillingDetailsForm.vue'
import type { PriceListItem } from '~/composables/api/price-list/useFetchPriceList'
import type BillingPaypalButton from '~/components/Billing/BillingPaypalButton.vue'
import type { EstimatePayload } from '~/composables/api/subscription/estimate/useFetchSubscriptionEstimate'
import type {
  CreateSubscriptionPayload,
  CreateSubscriptionPaymentIntentData,
  SubscriptionPaymentMethod,
  UpdateWorkspaceBillingPayload,
} from '~/composables/api/subscription/useFetchSubscription'

import CreditCard from '~/components/CreditCard.vue'
import { MissingPaymentIntentError } from '~/utils/errors'
import avainlippuImageSrc from '~/assets/images/avainlippu.svg?url'
import paymentSepaImageSrc from '~/assets/images/payment-sepa.svg?url'
import BillingSepaForm from '~/components/Billing/BillingSepaForm.vue'
import paymentCardsImageSrc from '~/assets/images/payment-cards.svg?url'
import paymentPaypalImageSrc from '~/assets/images/payment-paypal.svg?url'
import BillingInvoiceForm from '~/components/Billing/BillingInvoiceForm.vue'
import poweredByStripeImageSrc from '~/assets/images/powered-by-stripe.svg?url'

import type { SepaPaymentDetails } from '../Billing/BillingSepaForm.vue'

defineOptions({
  inheritAttrs: false,
})

const { t } = useI18n()
const { data: config } = useQueryConfig.current()
const { country, workspace, user, activeWorkspaceId } = useAuth()
const { formatDate, formatCurrency } = useIntl()
const { terms, privacyPolicy } = useLinks()
const { translateForCountry } = useCountryAwareTranslation()
const { data: reviewsData } = useQueryRating.getRating()

const currentDate = useNow({ interval: 60_000 })
const weekAfterCurrentDateFormatted = computed(() =>
  formatDate(dayjs(currentDate.value).add(7, 'day').format()))

const billingVatNumber = ref(workspace.value?.attributes.billingVatNumber ?? '')
const isVATInputVisible = ref(!!billingVatNumber.value)

const {
  selectedPlan,
  paymentPeriodUnit,
  priceList,
  discountCode,
  availablePaymentPeriodUnits,
  activationFailed,
  activationCompleted,
  showIntercom,
  isInterestedInLegalOnboarding,
  selectedAddonsForEstimate,
} = useSubscriptionProcessContext()

const invoicingSupplementAmount = computed(() =>
  paymentPeriodUnit.value === 'month' && !!config.value?.invoicing_supplement
    ? config.value.invoicing_supplement / 100
    : undefined,
)

const formattedInvoiceSupplementAmount = computed(() =>
  invoicingSupplementAmount.value
    ? formatCurrency(invoicingSupplementAmount.value, { minimumFractionDigits: 0 })
    : undefined,
)

const priceListVersion = computed(() => priceList.data.value?.data_version)
const userAddon = computed(() => priceList.data.value?.addons.find(addon => addon.addon_id === 'user'))

const priceListItems = computed(() => (priceList.dataWithoutUpgrades.value?.items ?? []))

const isPriceListPending = computed(() => priceList.isPending.value)

const selectedPlanId = computed({
  get: () => selectedPlan.value?.id,
  set: (value) => {
    selectedPlan.value = priceListItems.value.find(item => item.id === value)
  },
})

const selectedPlanPriceId = computed(() =>
  selectedPlan.value?.prices
    .find(({ period_unit }) => period_unit === paymentPeriodUnit.value)
    ?.id ?? '',
)

const hasSignatureFeatureConfig = computed(() =>
  !!priceList.dataWithoutUpgrades.value?.data?.feature_configs?.some(({ feature_id }) => feature_id === 'signing'),
)

const getItemPrice = (item: PriceListItem) => {
  const itemPriceAnnual = item.prices.find(({ period_unit }) => period_unit === 'year')
  const itemPriceMonthly = item.prices.find(({ period_unit }) => period_unit === 'month')
  const price = item.data.custom_monthly_price ?? itemPriceAnnual?.monthly_price ?? itemPriceMonthly?.monthly_price ?? 0

  return price / 100
}

const formatCurrencyWithoutFractions = (
  value: number,
  opts?: Parameters<typeof formatCurrency>[1],
) => formatCurrency(value, { minimumFractionDigits: 0, maximumFractionDigits: 0, ...opts })

const plans = computed(() => priceListItems.value.map(item => ({
  value: item.id,
  text: t('subscribe.cart.plan.title', {
    plan: item.title,
    price: formatCurrencyWithoutFractions(getItemPrice(item)),
  }),
})))

const selectedPlanText = computed(() => plans.value.find(({ value }) => value === selectedPlanId.value)?.text)

const paymentMethod = ref<SubscriptionPaymentMethod>('card')

onMounted(() => {
  paymentMethod.value = (workspace.value?.attributes.paymentMethod as SubscriptionPaymentMethod)
    || config.value?.payment_methods.subscriptions[0]
})

const isCancellationPolicyVisible = ref(false)

const billingDetails = ref<BillingDetails>({
  country: workspace.value?.attributes.billingCountry || country.value!,
  companyName: workspace.value?.attributes.name ?? workspace.value?.attributes?.displayName ?? '',
  companyIdent: workspace.value?.attributes.billingCompanyIdent ?? workspace.value?.attributes.ident ?? '',
  firstName: user.value?.attributes.firstName ?? '',
  lastName: user.value?.attributes.lastName ?? '',
  address: workspace.value?.attributes.billingAddress ?? workspace.value?.attributes.address ?? '',
  city: workspace.value?.attributes.billingCity ?? workspace.value?.attributes.city ?? '',
  postNumber: workspace.value?.attributes.billingPostCode ?? workspace.value?.attributes.postCode ?? '',
  email: workspace.value?.attributes.email ?? user.value?.attributes.email ?? '',
  phone: workspace.value?.attributes.billingPhone ?? '',
})

const invoicingData = ref<InvoicingData>({
  companyName: billingDetails.value.companyName ?? '',
  companyIdent: billingDetails.value.companyIdent ?? '',
  address: billingDetails.value.address ?? '',
  postNumber: billingDetails.value.postNumber ?? '',
  city: billingDetails.value.city ?? '',
  email: billingDetails.value.email ?? '',
  einvoiceAddress: '',
  einvoiceServiceId: '',
})

const sepaPaymentData = ref<SepaPaymentDetails>({
  firstName: billingDetails.value.firstName ?? '',
  lastName: billingDetails.value.lastName ?? '',
  email: billingDetails.value.email ?? '',
})

const copyBillingDataToInvoice = () => {
  const copiedKeys
  = ['companyName', 'companyIdent', 'address', 'city', 'postNumber', 'email'] satisfies (keyof BillingDetails & keyof InvoicingData)[]

  copiedKeys.forEach((key) => {
    if (billingDetails.value[key])
      invoicingData.value[key] = billingDetails.value[key] as string
  })
}

const copyBillingDataToSepa = () => {
  const copiedKeys
  = ['firstName', 'lastName', 'email'] satisfies (keyof BillingDetails & keyof SepaPaymentDetails)[]

  copiedKeys.forEach((key) => {
    if (billingDetails.value[key])
      sepaPaymentData.value[key] = billingDetails.value[key] as string
  })
}

const paymentPeriodOptions = computed(() => {
  if (!selectedPlan.value)
    return []

  const customMonthlyPrice = selectedPlan.value.data.custom_monthly_price

  if (customMonthlyPrice)
    return []

  const itemPriceAnnual
    = (selectedPlan.value.prices.find(({ period_unit }) => period_unit === 'year')?.monthly_price ?? 0) / 100
  const itemPriceMonthly
    = (selectedPlan.value.prices.find(({ period_unit }) => period_unit === 'month')?.monthly_price ?? 0) / 100

  const itemPriceAnnualPerYear = itemPriceAnnual * 12
  const itemPriceMonthlyPerYear = itemPriceMonthly * 12
  const savings = itemPriceMonthlyPerYear - itemPriceAnnualPerYear

  return [
    {
      value: 'year',
      isChecked: paymentPeriodUnit.value === 'year',
      title: priceListVersion.value === 'v2'
        ? t('subscribe.period.annual.title-v2')
        : priceListVersion.value === 'v1.1'
          ? t('subscribe.period.annual.title-v1-1')
          : t('subscribe.period.annual.title'),
      description:
        priceListVersion.value === 'v1.1'
          ? t('subscribe.payment-period.annual.description-v1-1', {
              annualPrice: formatCurrencyWithoutFractions(itemPriceAnnualPerYear),
              monthlyPrice: formatCurrencyWithoutFractions(itemPriceAnnual),
            })
          : t('subscribe.payment-period.annual.description', {
              annualPrice: formatCurrencyWithoutFractions(itemPriceAnnualPerYear),
              monthlyPrice: formatCurrencyWithoutFractions(itemPriceAnnual),
            }),
      savings: formatCurrencyWithoutFractions(savings, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    },
    {
      value: 'month',
      isChecked: paymentPeriodUnit.value === 'month',
      title: priceListVersion.value === 'v1.1' ? t('subscribe.period.monthly.title-v1-1') : t('subscribe.period.monthly.title'),
      description:
        priceListVersion.value === 'v2'
          ? t('subscribe.payment-period.monthly.description-v2', { monthlyPrice: formatCurrencyWithoutFractions(itemPriceMonthly) })
          : priceListVersion.value === 'v1.1'
            ? t('subscribe.payment-period.monthly.description-v1-1', {
                monthlyPrice: formatCurrencyWithoutFractions(itemPriceMonthly),
              })
            : t('subscribe.payment-period.monthly.description', {
                annualPrice: formatCurrencyWithoutFractions(itemPriceMonthlyPerYear),
                monthlyPrice: formatCurrencyWithoutFractions(itemPriceMonthly),
              }),
    },
  ].sort((a, _) => priceListVersion.value === 'v2' ? (a.value === 'month' ? -1 : 1) : 0)
})

const isIdentificationOpen = ref(false)

const canIdentify = computed(() => config.value?.feature_enabled_identification)

const isPaidViaPartner = computed(() => paymentMethod.value === 'partner')
const partner = computed(() => workspace.value?.relationships?.partner?.data)

const isCardPaymentsEnabled = computed(() => config.value?.payment_methods.subscriptions.includes('card'))

const isInvoicePaymentsEnabled = computed(() => config.value?.payment_methods.subscriptions.includes('invoice'))

const { enabled: isPaypalPaymentFeatureEnabled } = useFeatureEnabled('paypal-payment')
const isPaypalPaymentsEnabled = computed(() => isPaypalPaymentFeatureEnabled.value && config.value?.payment_methods.subscriptions.includes('paypal'))

const { enabled: isSepaPaymentFeatureEnabled } = useFeatureEnabled('sepaPayment')

const isSepaPaymentsEnabled = computed(() => isSepaPaymentFeatureEnabled.value && config.value?.payment_methods.subscriptions.includes('direct_debit'))

const invoiceForm = ref<InstanceType<typeof BillingInvoiceForm>>()
const sepaForm = ref<InstanceType<typeof BillingSepaForm>>()
const paypalPayment = useTemplateRef<typeof BillingPaypalButton>('paypalPayment')

const { enabled: isLegalOnboardingFeatureEnabled } = useFeatureEnabled('legalOnboarding')

const userShouldIdentify = computed(() =>
  !!(canIdentify.value
    && paymentMethod.value === 'invoice'
    && !user.value?.attributes.hasIdent),
)

const creditCardInput = ref<InstanceType<typeof CreditCard>>()

const estimatePayload = computed<EstimatePayload>(() => ({
  planPriceId: selectedPlanPriceId.value,
  coupon: discountCode.value || undefined,
  addons: selectedAddonsForEstimate.value,
}))

const {
  data: estimate,
  isPending: isEstimatePending,
  isError: isEstimateError,
} = useQuerySubscriptionEstimate.getEstimate({
  payload: estimatePayload,
  useQueryOptions: {
    enabled: () => !!selectedPlanPriceId.value,
  },
})

const {
  mutate: createSubscription,
  isPending: isCreatingSubscription,
  error: createSubscriptionError,
  isSuccess: isSubscriptionCreated,
} = useQuerySubscription.create()

const { isLoading, startLoading, stopLoading } = useLoadingCount()

const isInvalidVatNumberError = computed(() => {
  const errorValue = createSubscriptionError.value as FetchError | null

  return !!errorValue?.statusCode
    && [400, 422].includes(errorValue.statusCode)
    && errorValue.data.message === 'Invalid VAT number'
})
const isVatNumberDirty = ref(false)

const isInvalidBillingCompanyIdent = computed(() => {
  const errorValue = createSubscriptionError.value as FetchError | null

  return !!errorValue?.statusCode
    && errorValue.statusCode === 422
    && !!errorValue.data?.validationErrors?.billingCompanyIdent
})

const isSubmitInProgress = computed(() =>
  isCreatingSubscription.value || isLoading.value,
)

const isPaymentMethodValid = computed(() => {
  switch (paymentMethod.value) {
    case 'partner':
      return isPaidViaPartner.value
    case 'invoice':
      return invoiceForm.value?.isValid && !userShouldIdentify.value
    case 'direct_debit':
      return sepaForm.value?.isValid
    case 'card':
      return creditCardInput.value?.isCardEnterComplete
    case 'paypal':
      return paypalPayment.value?.isValid
    default:
      return false
  }
})

const isSubmitEnabled = computed(() =>
  !isEstimatePending.value
  && !isEstimateError.value
  && !isSubmitInProgress.value
  && isPaymentMethodValid.value,
)

const getUpdateWorkspacePayload = (): UpdateWorkspaceBillingPayload => {
  let updateWorkspacePayload: UpdateWorkspaceBillingPayload = {
    billingCountry: billingDetails.value.country,
    billingVatNumber: (billingVatNumber.value.trim() !== country.value && billingVatNumber.value.trim()) || null,
    billingPhone: billingDetails.value.phone ?? null,
  }

  if (paymentMethod.value === 'card' || paymentMethod.value === 'paypal') {
    updateWorkspacePayload = {
      ...updateWorkspacePayload,
      billingCompanyName: billingDetails.value.companyName,
      billingCompanyIdent: billingDetails.value.companyIdent,
      billingAddress: billingDetails.value.address,
      billingPostCode: billingDetails.value.postNumber,
      billingCity: billingDetails.value.city,
      email: billingDetails.value.email,
    }
  }
  else if (paymentMethod.value === 'direct_debit') {
    updateWorkspacePayload = {
      ...updateWorkspacePayload,
      billingCompanyName: billingDetails.value.companyName,
      billingCompanyIdent: billingDetails.value.companyIdent,
      billingAddress: billingDetails.value.address,
      billingPostCode: billingDetails.value.postNumber,
      billingCity: billingDetails.value.city,
      billingFirstName: sepaPaymentData.value.firstName,
      billingLastName: sepaPaymentData.value.lastName,
      email: sepaPaymentData.value.email,
    }
  }
  else {
    updateWorkspacePayload = {
      ...updateWorkspacePayload,
      billingCompanyName: invoicingData.value.companyName,
      billingCompanyIdent: invoicingData.value.companyIdent,
      billingAddress: invoicingData.value.address,
      billingPostCode: invoicingData.value.postNumber,
      billingCity: invoicingData.value.city,
      email: invoicingData.value.email,
      einvoiceAccount: invoicingData.value.einvoiceAddress,
      einvoiceOperator: invoicingData.value.einvoiceServiceId,
    }
  }

  return updateWorkspacePayload
}

const getStripePaymentIntentId = async () => {
  if (paymentMethod.value !== 'card' || !estimate.value?.amountDue || !creditCardInput.value)
    return undefined

  const stripePaymentIntentId = await creditCardInput.value.getPaymentIntent({ amount: estimate.value.amountDue })

  return {
    stripePaymentIntentId,
  }
}

const getSepaPaymentIntentId = async () => {
  if (paymentMethod.value !== 'direct_debit' || !estimate.value?.amountDue || !sepaForm.value)
    return undefined

  const chargebeePaymentIntent = await sepaForm.value.createPaymentIntent({
    amount: estimate.value.amountDue,
    currency_code: config.value?.currency_code || 'EUR',
  })

  if (!chargebeePaymentIntent)
    return undefined

  return {
    chargebeePaymentIntentId: chargebeePaymentIntent.id,
  }
}

const { warning } = useToast()

const trackActivationFailure = (
  errorMessage: ActivationFailureParams['errorMessage'] = 'Unknown error',
) => {
  activationFailed({
    planPriceId: selectedPlanPriceId.value,
    errorMessage,
  })
}

watch(isInvalidVatNumberError, (isError) => {
  if (!isError)
    return

  warning(t('vat.invalid-value'))
  trackActivationFailure('Invalid VAT number')
})

watch(createSubscriptionError, (error) => {
  if (!error || isInvalidVatNumberError.value || isInvalidBillingCompanyIdent.value)
    return

  console.error(error)
  warning(t('subscribe.cart.failure'))
  trackActivationFailure()
})

whenever(isInvalidBillingCompanyIdent, () => warning(translateForCountry('invalid_billing_company_ident')))

const trackActivation = () => {
  if (!selectedPlan.value)
    return

  activationCompleted({
    planPriceId: selectedPlanPriceId.value,
    plan: selectedPlan.value.title,
    annualPrice: getItemPrice(selectedPlan.value) * 12,
    couponId: estimate.value?.activeCoupon?.id,
    currency: config.value?.currency_code,
    paymentMethod: paymentMethod.value,
  })
}

whenever(isSubscriptionCreated, () => trackActivation(), { once: true })

const getPaymentIntentId = () => {
  if (paymentMethod.value === 'direct_debit')
    return getSepaPaymentIntentId()

  return getStripePaymentIntentId()
}

const storeSelectedPaymentMethod = async () => {
  if (paymentMethod.value === 'card')
    await creditCardInput.value?.save()
  else if (paymentMethod.value === 'direct_debit')
    await sepaForm.value?.save()
}

const submit = async (intent?: Record<'id', string>) => {
  if (!activeWorkspaceId.value || !isSubmitEnabled.value)
    return

  startLoading()
  let paymentIntentData: CreateSubscriptionPaymentIntentData | undefined

  try {
    if (estimate.value?.amountDue === 0)
      await storeSelectedPaymentMethod()
    else if (intent && intent.id)
      paymentIntentData = { chargebeePaymentIntentId: intent.id }
    else
      paymentIntentData = await getPaymentIntentId()

    if (!paymentIntentData && !(['invoice', 'partner'].includes(paymentMethod.value)) && estimate.value?.amountDue !== 0)
      throw new MissingPaymentIntentError()
  }
  catch (e) {
    if (e instanceof MissingPaymentIntentError)
      console.error(e)

    warning(t('subscribe.cart.invalidPaymentDetails'))
    return
  }
  finally {
    stopLoading()
  }

  isVatNumberDirty.value = false

  const payload = {
    planPriceId: selectedPlanPriceId.value,
    paymentMethod: paymentMethod.value,
    addons: selectedAddonsForEstimate.value,
    couponId: estimate.value?.activeCoupon?.id,
    ...(paymentIntentData ?? { stripePaymentIntentId: undefined, chargebeePaymentIntentId: undefined }),
    ...getUpdateWorkspacePayload(),
  } satisfies CreateSubscriptionPayload

  createSubscription({
    workspaceId: activeWorkspaceId.value,
    payload,
  })
}
</script>

<template>
  <CancellationPolicyModal
    v-if="isCancellationPolicyVisible"
    v-model="isCancellationPolicyVisible"
  />
  <div
    v-bind="$attrs"
    class="flex max-h-full flex-col overflow-y-auto overscroll-y-none"
  >
    <div
      class="me-auto ms-auto w-full max-w-[1120px] px-4 @container md:px-6"
    >
      <div
        class="mt-8 flex w-full flex-col flex-nowrap @2xl:flex-row md:mt-16"
      >
        <div class="flex w-full flex-col gap-8 @2xl:w-1/2">
          <h1
            class="mb-4 text-2xl font-semibold"
          >
            {{ t('subscribe.cart-title') }}
          </h1>

          <div class="flex flex-col gap-6">
            <template v-if="userAddon">
              <span class="text-xl font-semibold">{{ capitalize(t('subscribe.plans.userAddon.title')) }}</span>
              <SubscriptionCartUserAddon :disabled="isSubmitInProgress" />
            </template>

            <template v-else>
              <span class="text-xl font-semibold">{{ t('subscribe.plans.title') }}</span>
              <DTFieldSelect
                v-model="selectedPlanId"
                :disabled="isSubmitInProgress"
              >
                <DTFieldSelectButton class="text-gray-800">
                  <div
                    v-if="isPriceListPending"
                    class="flex h-[1lh] items-center"
                  >
                    <DTLoaderDots />
                  </div>
                  <template v-else>
                    {{ selectedPlanText }}
                  </template>
                </DTFieldSelectButton>
                <DTFieldSelectOptions>
                  <DTFieldSelectOption
                    v-for="{ value, text } in plans"
                    :key="value"
                    :text
                    :value
                  />
                </DTFieldSelectOptions>
              </DTFieldSelect>
            </template>

            <div
              v-if="availablePaymentPeriodUnits.length > 1"
              class="space-y-2"
            >
              <span class="font-semibold">{{ t('subscribe.period.title') }}</span>
              <DTRadioGroup
                v-model="paymentPeriodUnit"
                class="!border-0"
              >
                <DTRadioGroupOption
                  v-for="opt, index in paymentPeriodOptions"
                  :key="opt.value"
                  :value="opt.value"
                  class="relative !items-start !border"
                  :disabled="isSubmitInProgress"
                  :class="[
                    opt.isChecked ? '!border-gray-800' : 'border-gray-200',
                    index === 0
                      ? 'rounded-t-lg'
                      : index === paymentPeriodOptions.length - 1
                        ? 'rounded-b-lg'
                        : '',
                  ]"
                >
                  <div class="mr-2 flex flex-col">
                    <div class="flex items-center gap-2">
                      <span class="font-semibold">{{ opt.title }}</span>
                      <DTBadge
                        v-if="opt.savings"
                        variant="green"
                        class="text-3xs"
                      >
                        {{ t('save-amount', { amount: opt.savings }) }}
                      </DTBadge>
                    </div>
                    <span class="text-sm text-gray-600">
                      {{ opt.description }}
                      <span
                        v-if="opt.value === 'month' && priceListVersion === 'v1.1'"
                        class="text-xs text-gray-500"
                      >
                        ({{ t('subscribe.payment-period.monthly.cancelAnytime') }})
                      </span>
                    </span>
                  </div>
                </DTRadioGroupOption>
              </DTRadioGroup>
            </div>
          </div>

          <template v-if="hasSignatureFeatureConfig && ['FI', 'SE'].includes(country ?? '')">
            <DTDivider />

            <SubscriptionCartSignatureAddon :disabled="isSubmitInProgress" />
          </template>

          <template v-else-if="priceListVersion === 'v1.1'">
            <DTDivider />

            <SubscriptionCartAddonSelector :disabled="isSubmitInProgress" />
          </template>

          <DTDivider />

          <template v-if="isLegalOnboardingFeatureEnabled">
            <DTCheck
              v-model="isInterestedInLegalOnboarding"
              class="[&_label]:!w-auto"
              :disabled="isSubmitInProgress"
            >
              <template #label>
                <span class="inline-block text-gray-800">
                  {{ t('subscribe.legalOnboarding.label') }}
                  <DTTooltip
                    variant="dark"
                    placement="bottom"
                    :content="t('subscribe.legalOnboarding.info')"
                  >
                    <LineInfoCircle
                      class="inline size-4 shrink-0"
                    />
                  </DTTooltip>
                </span>
              </template>
            </DTCheck>

            <DTDivider />
          </template>

          <div class="flex flex-col gap-6">
            <span class="text-xl font-semibold">{{ t('billing-details') }}</span>

            <BillingDetailsForm
              id="billing-details-form"
              v-model="billingDetails"
              class="gap-3 @3xl:grid-cols-2"
              :disabled="isSubmitInProgress"
              @submit.prevent="submit()"
            />

            <DTButton
              v-if="!isVATInputVisible"
              variant="link"
              size="sm"
              dense
              class="max-w-max"
              :disabled="isSubmitInProgress"
              @click="() => {
                isVATInputVisible = true
                billingVatNumber = country ?? ''
              }"
            >
              {{ t('subscribe.vat.add-text') }}
            </DTButton>

            <DTField
              v-else
              :label="t('vat.input-label')"
              :description="t('vat.input-placeholder')"
              :error="isInvalidVatNumberError && !isVatNumberDirty ? t('vat.invalid-value') : undefined"
            >
              <DTFieldInput
                v-model="billingVatNumber"
                :disabled="isSubmitInProgress"
                @update:model-value="isVatNumberDirty = true"
              />
            </DTField>
          </div>

          <DTDivider />

          <div class="flex flex-col gap-6">
            <div class="flex flex-col">
              <span class="text-xl font-semibold">{{ t('payment') }}</span>
              <span
                v-if="!isPaidViaPartner"
                class="text-sm text-gray-600"
              >{{ t('subscribe.payment.subtitle') }}</span>
            </div>

            <DTAlert
              v-if="isPaidViaPartner && partner"
              variant="blue"
            >
              <i18n-t
                keypath="subscribe.payment.partner-message"
                tag="span"
              >
                <template #partner>
                  <strong>{{ partner.attributes.name }}</strong>
                </template>
              </i18n-t>
            </DTAlert>

            <div class="divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200 empty:hidden">
              <ContentGroupToggleable
                v-if="isCardPaymentsEnabled"
                :model-value="paymentMethod === 'card'"
                panel-id="payment_method_panel--card"
                use-show
                class="!gap-0"
              >
                <template #title="{ isOpen }">
                  <div
                    class="relative flex flex-1 justify-between gap-2 p-3.5"
                  >
                    <DTRadio
                      v-model="paymentMethod"
                      name="payment_method"
                      value="card"
                      :disabled="isSubmitInProgress"
                      :label="t('payment-method.card')"
                      label-class="before:absolute before:inset-0 cursor-pointer"
                      class="!static"
                      aria-controls="payment_method_panel--card"
                      :aria-expanded="isOpen"
                    />
                    <img :src="paymentCardsImageSrc">
                  </div>
                </template>

                <div
                  class="
                      flex
                      flex-col
                      gap-2.5
                      border-t
                      border-t-gray-200
                      bg-gray-50
                      p-3.5
                      "
                  :class="{
                    'rounded-b-lg': !isInvoicePaymentsEnabled,
                  }"
                >
                  <CreditCard
                    ref="creditCardInput"
                    card-class="bg-white"
                    :disabled="isSubmitInProgress"
                  />
                  <div class="flex items-center justify-between gap-2">
                    <span class="text-xs text-gray-600">
                      {{ t('subscribe.payment.card-info') }}
                    </span>
                    <img :src="poweredByStripeImageSrc">
                  </div>
                </div>
              </ContentGroupToggleable>

              <ContentGroupToggleable
                v-if="isPaypalPaymentsEnabled"
                :model-value="paymentMethod === 'paypal'"
                panel-id="payment_method_panel--paypal"
                use-show
                class="!gap-0"
              >
                <template #title="{ isOpen }">
                  <div
                    class="relative flex flex-1 items-center justify-between gap-2 p-3.5"
                  >
                    <DTRadio
                      v-model="paymentMethod"
                      name="payment_method"
                      value="paypal"
                      :disabled="isSubmitInProgress"
                      label="PayPal"
                      label-class="before:absolute before:inset-0 cursor-pointer"
                      class="!static"
                      aria-controls="payment_method_panel--sepa"
                      :aria-expanded="isOpen"
                    />
                    <img
                      :src="paymentPaypalImageSrc"
                      class="h-5"
                    >
                  </div>
                </template>

                <div
                  class="
                      flex
                      flex-col
                      gap-2.5
                      border-t
                      border-t-gray-200
                      bg-gray-50
                      p-3.5
                      pb-7
                      "
                  :class="{
                    'rounded-b-lg': !isInvoicePaymentsEnabled,
                  }"
                >
                  <i18n-t
                    keypath="subscribe.cart.paypal.paymentMessage"
                    class="text-sm text-gray-600"
                    tag="span"
                  >
                    <template #label>
                      <strong>{{ t('subscribe.cart.paypal.buttonLabel') }}</strong>
                    </template>
                  </i18n-t>
                </div>
              </ContentGroupToggleable>

              <ContentGroupToggleable
                v-if="isSepaPaymentsEnabled"
                :model-value="paymentMethod === 'direct_debit'"
                panel-id="payment_method_panel--sepa"
                use-show
                class="!gap-0"
              >
                <template #title="{ isOpen }">
                  <div
                    class="relative flex flex-1 justify-between gap-2 p-3.5"
                  >
                    <DTRadio
                      v-model="paymentMethod"
                      name="payment_method"
                      value="direct_debit"
                      :disabled="isSubmitInProgress"
                      :label="t('payment-method.directDebit')"
                      label-class="before:absolute before:inset-0 cursor-pointer"
                      class="!static"
                      aria-controls="payment_method_panel--sepa"
                      :aria-expanded="isOpen"
                    />
                    <img :src="paymentSepaImageSrc">
                  </div>
                </template>

                <div
                  class="
                      flex
                      flex-col
                      gap-2.5
                      border-t
                      border-t-gray-200
                      bg-gray-50
                      p-3.5
                      pb-7
                      "
                  :class="{
                    'rounded-b-lg': !isInvoicePaymentsEnabled,
                  }"
                >
                  <DTButton
                    size="xs"
                    variant="link"
                    dense
                    class="me-0 ms-auto"
                    @click="copyBillingDataToSepa"
                  >
                    {{ t('subscribe.cart.copy-billing-details') }}
                  </DTButton>

                  <BillingSepaForm
                    ref="sepaForm"
                    v-model="sepaPaymentData"
                    :disabled="isSubmitInProgress"
                  />
                </div>
              </ContentGroupToggleable>

              <ContentGroupToggleable
                v-if="isInvoicePaymentsEnabled"
                :model-value="paymentMethod === 'invoice'"
                panel-id="payment_method_panel--invoice"
                use-show
                class="!gap-0 rounded-b-lg"
              >
                <template #title="{ isOpen }">
                  <div
                    class="relative flex flex-1 justify-between gap-2 p-3.5"
                    aria-controls="payment_method_panel--invoice"
                    :aria-expanded="isOpen"
                  >
                    <DTRadio
                      v-model="paymentMethod"
                      name="payment_method"
                      value="invoice"
                      :disabled="isSubmitInProgress"
                      :label="t('payment-method.invoice')"
                      label-class="before:absolute before:inset-0 cursor-pointer"
                      class="!static"
                    />
                  </div>
                </template>
                <div
                  v-if="userShouldIdentify"
                  class="p-3.5"
                >
                  <DTAlert variant="yellow">
                    <p>
                      {{ t('subscribe.identify.message') }}
                      <DTButton
                        variant="link"
                        size="sm"
                        dense
                        class="!text-gray-500 hover:!text-gray-800"
                        :disabled="isSubmitInProgress"
                        @click="isIdentificationOpen = true"
                      >
                        {{ t('subscribe.identify.cta') }}
                      </DTButton>
                    </p>
                  </DTAlert>
                  <LazyIdentification
                    v-if="isIdentificationOpen"
                    v-model="isIdentificationOpen"
                  />
                </div>

                <div
                  v-else
                  class="
                      flex
                      flex-col
                      gap-2.5
                      rounded-b-lg
                      border-t
                      border-t-gray-200
                      bg-gray-50
                      p-3.5
                      "
                  :class="{
                    'rounded-t-lg': !isCardPaymentsEnabled,
                  }"
                >
                  <DTAlert
                    v-if="invoicingSupplementAmount"
                    variant="blue"
                  >
                    <LineAlertCircle class="size-4 shrink-0" />
                    <span>
                      {{ t('subscribe.cart.invoice-supplement', {
                        price: formattedInvoiceSupplementAmount,
                      })
                      }}
                    </span>
                  </DTAlert>

                  <DTButton
                    size="xs"
                    variant="link"
                    dense
                    class="me-0 ms-auto"
                    @click="copyBillingDataToInvoice"
                  >
                    {{ t('subscribe.cart.copy-billing-details') }}
                  </DTButton>

                  <BillingInvoiceForm
                    ref="invoiceForm"
                    v-model="invoicingData"
                    :disabled="isSubmitInProgress"
                    class="gap-x-2.5 gap-y-3 @3xl:grid-cols-2"
                    @submit.prevent
                  />
                </div>
              </ContentGroupToggleable>
            </div>
          </div>

          <DTDivider />

          <div class="w-full">
            <div class="mb-6 flex flex-col gap-1">
              <span class="flex size-[30px] items-center justify-center rounded-full bg-green-500">
                <LineReverseLeft class="size-4 text-white" />
              </span>
              <p class="text-xl font-semibold">
                {{ t('subscribe.money-back-guarantee') }}
              </p>
              <p class="text-sm text-gray-600">
                {{ t('subscribe.cart.cancelBefore', { date: weekAfterCurrentDateFormatted }) }}
              </p>
            </div>

            <p class="text-sm text-gray-600">
              {{ t('subscribe.cart.refundText') }}
            </p>
          </div>

          <DTDivider class="@2xl:hidden" />

          <div class="w-full space-y-4 @2xl:hidden">
            <SubscriptionCartSummary
              v-model="discountCode"
              :selected-plan-price-id="selectedPlanPriceId"
              :disabled="isSubmitInProgress"
            />
          </div>

          <div class="mb-8 mt-12 flex flex-col gap-5">
            <DTAlert
              v-if="isEstimateError"
              variant="red"
            >
              <LineAlertCircle class="size-4 shrink-0" />
              <i18n-t
                tag="p"
                keypath="subscribe.cart.estimate-error"
              >
                <template #chatTrigger>
                  <DTButton
                    variant="link"
                    size="sm"
                    dense
                    class="!text-red-700 hover:!text-red-800"
                    @click="showIntercom"
                  >
                    {{ t('chat.text') }}
                  </DTButton>
                </template>
              </i18n-t>
            </DTAlert>

            <div
              v-if="isPaypalPaymentsEnabled"
              class="flex items-center gap-2"
            >
              <BillingPaypalButton
                v-show="paymentMethod === 'paypal'"
                ref="paypalPayment"
                class="w-full"
                :amount-to-pay="estimate?.amountDue"
                :disabled="!isSubmitEnabled || paymentMethod !== 'paypal'"
                @payment-intent-authorized="submit"
              />
              <DTSpinner
                v-if="isSubmitInProgress && paymentMethod === 'paypal'"
                class="shrink-0 grow-0"
              />
            </div>

            <DTButton
              v-if="paymentMethod !== 'paypal'"
              variant="blue"
              class="w-full"
              form="billing-details-form"
              type="submit"
              :disabled="!isSubmitEnabled"
            >
              {{ t('subscribe.payment.cta') }}
              <DTSpinner
                v-if="isSubmitInProgress"
                :size="4"
                :weight="2"
                variant="white"
              />
            </DTButton>
          </div>
        </div>

        <div class="hidden @2xl:block @2xl:w-1/2">
          <div
            class="sticky top-10 space-y-4 @2xl:ml-6 @4xl:ml-24"
          >
            <SubscriptionCartSummary
              v-model="discountCode"
              :selected-plan-price-id="selectedPlanPriceId"
              :disabled="isSubmitInProgress"
            />
            <SubscriptionRefLogos
              class="!mt-12 w-full"
              tight
            />
          </div>
        </div>
      </div>
      <div class="mb-10 flex flex-col gap-8 py-8 md:mb-0">
        <div class="flex items-center gap-12">
          <GoogleReviewBadge
            v-if="reviewsData?.data"
            :user-rating-count="reviewsData.data.user_rating_count"
            :reviews-uri="reviewsData.data.reviews_uri"
            :rating="reviewsData.data.rating"
          />
          <img
            v-if="country === 'FI'"
            :src="avainlippuImageSrc"
            class="h-12 w-auto"
          >
        </div>
        <div class="flex items-center gap-2">
          <DTButton
            variant="link"
            size="sm"
            dense
            external
            target="_blank"
            :to="privacyPolicy"
          >
            {{ t('privacy-policy') }}
          </DTButton>

          <DTButton
            variant="link"
            size="sm"
            dense
            external
            target="_blank"
            :to="terms"
          >
            {{ t('terms-of-service') }}
          </DTButton>
        </div>
      </div>
    </div>

    <div class="hidden bg-gray-50 py-20 md:block">
      <div class="me-auto ms-auto w-full max-w-[1120px]">
        <SubscriptionTestimonials />
      </div>
    </div>
  </div>
</template>
